/**
 * Pure helpers for Kanban drag-and-drop resolution and status updates.
 */

import { arrayMove } from '@dnd-kit/sortable';
import { extractColumnIdFromDropTarget } from '../../utils';
import { canMoveToStatus } from '../../../../../utils/wipLimits';

/**
 * Build frozen task counts and hidden-count snapshot for drag performance.
 */
export const buildDragFreezeSnapshot = (tasksByStatus, calculateHiddenCounts, calculateVisibleCardCount) => ({
  taskCounts: Object.fromEntries(
    Object.keys(tasksByStatus).map((columnId) => [
      columnId,
      (tasksByStatus[columnId] || []).length,
    ]),
  ),
  hiddenCounts: calculateHiddenCounts(),
  visibleCardCount: calculateVisibleCardCount(),
});

/**
 * Resolve the column ID when reordering columns during drag.
 */
export const resolveColumnReorderTarget = (over, overData, findColumn, columnIds) => {
  const overId = over.id;

  if (overData?.type === 'column') {
    return columnIds.includes(overId) ? overId : null;
  }

  let targetColumnId = extractColumnIdFromDropTarget(overId);
  if (!targetColumnId && overData?.type === 'card') {
    targetColumnId = findColumn(overId);
  }

  return targetColumnId && columnIds.includes(targetColumnId) ? targetColumnId : null;
};

/**
 * Reorder column IDs and invoke the callback when indices differ.
 */
export const reorderColumnsIfNeeded = (columnIds, activeId, targetColumnId, onColumnReorder) => {
  if (!targetColumnId || activeId === targetColumnId) return;

  const oldIndex = columnIds.indexOf(activeId);
  const newIndex = columnIds.indexOf(targetColumnId);

  if (oldIndex !== -1 && newIndex !== -1) {
    onColumnReorder?.(arrayMove(columnIds, oldIndex, newIndex));
  }
};

/**
 * Resolve card drop destination column and index during drag-over.
 */
export const resolveCardDropDestination = (
  overId,
  columnIds,
  findColumn,
  getPositionIndex,
  taskIdsByColumn,
) => {
  const extractedColumnId = extractColumnIdFromDropTarget(overId);

  if (extractedColumnId) {
    return {
      destColumnId: extractedColumnId,
      dropIndex: taskIdsByColumn[extractedColumnId]?.length || 0,
    };
  }

  if (columnIds.includes(overId)) {
    return {
      destColumnId: overId,
      dropIndex: taskIdsByColumn[overId]?.length || 0,
    };
  }

  const destColumnId = findColumn(overId);
  if (!destColumnId) {
    return { destColumnId: null, dropIndex: null };
  }

  return {
    destColumnId,
    dropIndex: getPositionIndex(overId, destColumnId),
  };
};

/**
 * Resolve card drop column on drag-end (index comes from drag-over tracking).
 */
export const resolveCardDropColumn = (overId, columnIds, findColumn) => {
  const extractedColumnId = extractColumnIdFromDropTarget(overId);
  if (extractedColumnId) return extractedColumnId;
  if (columnIds.includes(overId)) return overId;
  return findColumn(overId);
};

export const findTaskInBoard = (tasksByStatus, taskId) =>
  Object.values(tasksByStatus).flat().find((t) => t.id === taskId);

/**
 * Get WIP check result including reason when blocked.
 */
export const getWipMoveCheck = (destColumnId, tasksByStatus, statuses, activeId) => {
  const allTasks = Object.values(tasksByStatus).flat();
  return canMoveToStatus(destColumnId, allTasks, statuses, activeId);
};

/**
 * Apply status change, handling nested sprint auto-sync atomically when needed.
 */
export const applyCardStatusUpdate = (
  activeId,
  destColumnId,
  dropIndex,
  tasksByStatus,
  onStatusChange,
  onSetStatusAutoSync,
) => {
  const task = findTaskInBoard(tasksByStatus, activeId);

  if (task?.nestedSprintId && task.statusAutoSync !== false && onSetStatusAutoSync) {
    onSetStatusAutoSync(activeId, false, destColumnId);
  } else {
    onStatusChange?.(activeId, destColumnId, null, dropIndex);
  }
};
